/**
 * Utility functions for handling attachment URLs and signed URL management
 */

import { config } from "../config";

/**
 * Extract attachment ID from a URL that contains attachment reference
 * @param {string} url - URL that may contain #attachment-{id} fragment
 * @returns {string|null} - Attachment ID or null if not found
 */
export function extractAttachmentId(url) {
    if (!url) return null;
    const match = url.match(/#attachment-(.+)$/);
    return match ? match[1] : null;
}

/**
 * Extract card ID from an attachment URL
 * @param {string} url - Attachment URL containing card ID
 * @returns {string|null} - Card ID or null if not found
 */
export function extractCardId(url) {
    if (!url) return null;
    const match = url.match(/\/cards\/([^\/]+)\//);
    return match ? match[1] : null;
}

/**
 * Create an image URL with embedded attachment ID for tracking
 * @param {string} signedUrl - The signed URL from the backend
 * @param {string} attachmentId - The attachment ID to embed
 * @returns {string} - URL with embedded attachment ID
 */
export function createImageUrlWithId(signedUrl, attachmentId) {
    if (!signedUrl || !attachmentId) return signedUrl;
    return `${signedUrl}#attachment-${attachmentId}`;
}

/**
 * Remove attachment ID fragment from URL to get clean signed URL
 * @param {string} url - URL that may contain attachment ID fragment
 * @returns {string} - Clean URL without fragment
 */
export function cleanAttachmentUrl(url) {
    if (!url) return url;
    return url.split('#')[0];
}

/**
 * Fetch a fresh signed URL for an attachment
 * @param {string} cardId - Card ID
 * @param {string} attachmentId - Attachment ID
 * @returns {Promise<string|null>} - Fresh signed URL or null if failed
 */
export async function fetchAttachmentUrl(cardId, attachmentId) {
    try {
        const response = await fetch(`${config.API_URI}/v1/cards/attachments/${cardId}/${attachmentId}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch attachment URL: ${response.status}`);
        }

        const data = await response.json();
        return data.attachment.url;
    } catch (error) {
        console.error("Error fetching attachment URL:", error);
        return null;
    }
}

/**
 * Refresh an attachment URL by fetching a new signed URL
 * @param {string} currentUrl - Current URL that may be expired
 * @returns {Promise<string|null>} - Fresh URL with attachment ID or null if failed
 */
export async function refreshAttachmentUrl(currentUrl) {
    const attachmentId = extractAttachmentId(currentUrl);
    const cardId = extractCardId(currentUrl);
    
    if (!attachmentId || !cardId) {
        console.warn("Cannot refresh URL: missing attachment ID or card ID");
        return null;
    }

    try {
        const freshUrl = await fetchAttachmentUrl(cardId, attachmentId);
        if (freshUrl) {
            return createImageUrlWithId(freshUrl, attachmentId);
        }
        return null;
    } catch (error) {
        console.error("Failed to refresh attachment URL:", error);
        return null;
    }
}

/**
 * Check if a URL is an attachment URL that can be refreshed
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL can be refreshed
 */
export function isRefreshableAttachmentUrl(url) {
    return !!(extractAttachmentId(url) && extractCardId(url));
}

/**
 * Create a cache key for tracking refresh attempts
 * @param {string} url - URL to create cache key for
 * @returns {string} - Cache key
 */
export function createRefreshCacheKey(url) {
    const attachmentId = extractAttachmentId(url);
    const cardId = extractCardId(url);
    return `${cardId}-${attachmentId}`;
}
